import type { AcademicReview, AppState, ScientificFrame, ScientificModelId, UncertaintyBudget } from './types';

interface AuditDefinition {
  status: AcademicReview['status'];
  auditId: string;
  benchmarkIds: string[];
  limitations: string[];
}

const REVIEWED_ON = '2026-07-14';

const AUDITS: Record<ScientificModelId, AuditDefinition> = {
  'free-space-band-limited-em-v1': {
    status: 'primary-source-reviewed',
    auditId: 'audit-free-space-em-v1',
    benchmarkIds: ['vacuum-spectrum-1thz', 'vacuum-spectrum-10thz', 'thermal-occupation-1thz-300k', 'thermal-occupation-1thz-4k', 'transverse-correlation-coincident'],
    limitations: [
      'Homogeneous isotropic free space only.',
      'Finite detector bands are ideal rectangular or Gaussian filters.',
      'Material dispersion, electro-optic transfer functions, finite probe volumes, and apparatus systematics are not included.'
    ]
  },
  'dce-open-waveguide-v1': {
    status: 'primary-source-reviewed',
    auditId: 'audit-dce-open-waveguide-v1',
    benchmarkIds: ['dce-johansson-2010-reference', 'dce-zero-modulation'],
    limitations: [
      'Perturbative open-waveguide approximation.',
      'The SQUID is represented as an effective time-dependent boundary.',
      'Amplifier chain, calibration uncertainty, nonlinear corrections, and full experimental covariance are not modeled.'
    ]
  },
  'squeezed-homodyne-v1': {
    status: 'primary-source-reviewed',
    auditId: 'audit-squeezed-homodyne-v1',
    benchmarkIds: ['squeezed-vacuum-0db', 'squeezed-vacuum-6db'],
    limitations: [
      'Ideal Gaussian single-mode covariance model.',
      'Detection loss is represented by vacuum mixing.',
      'No electronics noise, frequency-dependent loss, or nonlinear-crystal transfer function.'
    ]
  },
  'dynamic-boundary-parametric-v1': {
    status: 'historical-model-reviewed',
    auditId: 'audit-dynamic-boundary-parametric-v1',
    benchmarkIds: ['dynamic-boundary-zero-modulation'],
    limitations: [
      'Single-mode rotating-wave effective Hamiltonian.',
      'Small sinusoidal modulation and phenomenological quality factor.',
      'Not Moore’s exact arbitrary-trajectory solution and not a multimode cavity solver.'
    ]
  },
  'static-casimir-ideal-v1': {
    status: 'historical-model-reviewed',
    auditId: 'audit-static-casimir-ideal-v1',
    benchmarkIds: ['casimir-ideal-plates-100nm', 'casimir-pressure-scaling-100-to-200nm'],
    limitations: [
      'Perfectly conducting, zero-temperature idealization for the parallel-plate result.',
      'Sphere-plane force uses the proximity-force approximation.',
      'Finite conductivity, roughness, patch potentials, geometry corrections, and thermal corrections are not included.'
    ]
  },
  'lamb-shift-foundations-v1': {
    status: 'historical-model-reviewed',
    auditId: 'audit-lamb-shift-foundations-v1',
    benchmarkIds: ['lamb-leading-log-calibrated', 'lamb-shift-energy-conversion'],
    limitations: [
      'Leading-log historical teaching estimate with an explicit cutoff ratio.',
      'The calibrated cutoff is not a modern bound-state QED calculation.',
      'Fine, hyperfine, recoil, radiative, and proton-structure corrections are outside scope.'
    ]
  }
};

const finiteNonnegative = (value: number): number => Number.isFinite(value) && value >= 0 ? value : 0;

const buildUncertainty = (state: Readonly<AppState>, frame: ScientificFrame): UncertaintyBudget => {
  const numerical = finiteNonnegative(frame.validation.relativeIntegrationError);
  const statistical = frame.adapterId === 'modern' && frame.observables.detectorRmsVPerM > 0
    ? finiteNonnegative(frame.observables.detectorStandardErrorVPerM / frame.observables.detectorRmsVPerM)
    : null;
  const combined = Math.hypot(numerical, statistical ?? 0);
  const detectorNote = state.era === 'modern'
    ? 'The quantified statistical term is the ideal detector standard error divided by detector RMS.'
    : 'No experimental statistical uncertainty is assigned to this idealized historical adapter.';
  return {
    numericalRelative: numerical,
    statisticalRelative: statistical,
    combinedQuantifiedRelative: combined,
    confidenceLevel: 'not-assigned',
    modelDiscrepancyQuantified: false,
    coverage: `${detectorNote} The combined value includes only quantified numerical and ideal sampling terms. Model discrepancy, apparatus calibration, material response, and other systematic uncertainties are not quantified.`
  };
};

export const applyResearchReleaseReview = (state: Readonly<AppState>, frame: ScientificFrame): ScientificFrame => {
  const audit = AUDITS[frame.modelId];
  return {
    ...frame,
    uncertainty: buildUncertainty(state, frame),
    academicReview: {
      status: audit.status,
      auditId: audit.auditId,
      reviewedOn: REVIEWED_ON,
      benchmarkIds: [...audit.benchmarkIds],
      sourceIds: [...new Set(frame.validation.sourceIds)],
      limitations: [...audit.limitations]
    }
  };
};
